/**
 * Create folder modal
 */

import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, TreeSelect, Radio, Checkbox, message } from 'antd';
import { folderApi } from '../../services/resourceService';
import { ResourceScope, ResourceFolder, FolderType } from '../../types/resource';

const { Option } = Select;

interface CreateFolderModalProps {
  visible: boolean;
  folderType: FolderType;
  scope: ResourceScope;
  onCancel: () => void;
  onSuccess: () => void;
}

const CreateFolderModal: React.FC<CreateFolderModalProps> = ({
  visible,
  folderType,
  scope,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [folders, setFolders] = useState<ResourceFolder[]>([]);
  const [visibleToAll, setVisibleToAll] = useState(true);

  // Load folders
  useEffect(() => {
    if (visible) {
      loadFolders();
      form.setFieldsValue({ 
        scope,
        visible_to_all: true,
      });
      setVisibleToAll(true);
    }
  }, [visible, scope]);

  const loadFolders = async () => {
    try {
      const result = await folderApi.getTree(folderType, scope);
      setFolders(result);
    } catch (error) {
      console.error('Failed to load folders:', error);
    }
  };

  // Convert folders to tree data
  const buildTreeData = (folders: ResourceFolder[]): any[] => {
    return folders.map((folder) => ({
      title: folder.name,
      value: folder.id,
      children: folder.children ? buildTreeData(folder.children) : [],
    }));
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      await folderApi.create({
        name: values.name,
        parent_id: values.parent_id || null,
        folder_type: folderType,
        scope: scope,
        sort_order: 0,
        visible_to_all: values.visible_to_all,
        visible_roles: values.visible_to_all ? null : (values.visible_roles || []),
      });

      message.success('创建成功');
      form.resetFields();
      onSuccess();
    } catch (error: any) {
      if (error.errorFields) {
        return;
      }
      message.error(error.message || '创建失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="添加分组"
      open={visible}
      onOk={handleSubmit}
      onCancel={onCancel}
      confirmLoading={loading}
      width={500}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ scope, visible_to_all: true }}
      >
        <Form.Item
          name="name"
          label="分组名称"
          rules={[{ required: true, message: '请输入分组名称' }]}
        >
          <Input placeholder="请输入分组名称" />
        </Form.Item>

        <Form.Item name="parent_id" label="父分组">
          <TreeSelect
            placeholder="请选择父分组（可选，不选则为顶级分组）"
            allowClear
            treeData={buildTreeData(folders)}
          />
        </Form.Item>

        {/* Only show permission settings for enterprise folders */}
        {scope === ResourceScope.ENTERPRISE && (
          <>
            <Form.Item
              name="visible_to_all"
              label="可见权限"
            >
              <Radio.Group onChange={(e) => setVisibleToAll(e.target.value)}>
                <Radio value={true}>全员可见</Radio>
                <Radio value={false}>指定角色</Radio>
              </Radio.Group>
            </Form.Item>

            {!visibleToAll && (
              <Form.Item
                name="visible_roles"
                label="可见角色"
                rules={[{ required: true, message: '请选择至少一个角色' }]}
              >
                <Checkbox.Group>
                  <Checkbox value="admin">管理员</Checkbox>
                  <Checkbox value="lawyer">律师</Checkbox>
                  <Checkbox value="finance">财务</Checkbox>
                  <Checkbox value="sales">销售</Checkbox>
                </Checkbox.Group>
              </Form.Item>
            )}
          </>
        )}
      </Form>
    </Modal>
  );
};

export default CreateFolderModal;
